import { useEffect, useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import { useSetPassword, useSupabaseAccessToken } from "ra-supabase-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Layout } from "@/components/supabase/layout";

import { isDemoMode } from "@/components/atomic-crm/mfa/constants";
import { needsPasswordMfaStepUp } from "@/components/atomic-crm/mfa/mfaAuthGuard";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface FormData {
  password: string;
  confirmPassword: string;
}

type Step = "loading" | "mfa" | "password";

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>("loading");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  const access_token = useSupabaseAccessToken();
  const refresh_token = useSupabaseAccessToken({
    parameterName: "refresh_token",
  });

  const notify = useNotify();
  const translate = useTranslate();
  const [, { mutateAsync: setPassword }] = useSetPassword();

  useEffect(() => {
    if (!access_token || !refresh_token) return;

    let cancelled = false;

    const prepareSession = async () => {
      try {
        if (isDemoMode()) {
          if (!cancelled) setStep("password");
          return;
        }

        const supabase = getSupabaseClient();
        const { error: sessionError } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });
        if (sessionError) throw sessionError;

        const [
          { data: aalData, error: aalError },
          { data: factorsData, error: factorsError },
        ] = await Promise.all([
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          supabase.auth.mfa.listFactors(),
        ]);

        if (aalError || factorsError) {
          throw aalError ?? factorsError;
        }

        const factors = {
          totp: factorsData?.totp ?? [],
          phone: factorsData?.phone ?? [],
        };

        if (
          needsPasswordMfaStepUp({
            currentLevel: aalData?.currentLevel ?? null,
            factors,
          })
        ) {
          const verified = [...factors.totp, ...factors.phone].find(
            (factor) => factor.status === "verified",
          );
          if (!cancelled) {
            setFactorId(verified?.id ?? null);
            setStep("mfa");
          }
          return;
        }

        if (!cancelled) setStep("password");
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : "Failed to prepare session";
        notify(message, { type: "warning" });
        // Still show password form; setPassword will surface AAL2 errors if needed.
        setStep("password");
      }
    };

    void prepareSession();
    return () => {
      cancelled = true;
    };
  }, [access_token, refresh_token, notify]);

  const validate = (values: FormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    return {};
  };

  if (!access_token || !refresh_token) {
    if (process.env.NODE_ENV === "development") {
      console.error("Missing access_token or refresh_token for set password");
    }
    return (
      <Layout>
        <p>{translate("ra-supabase.auth.missing_tokens")}</p>
      </Layout>
    );
  }

  const submitMfa: SubmitHandler<FieldValues> = async (values) => {
    if (!factorId) {
      notify("crm.mfa.no_factor", {
        type: "error",
        messageArgs: {
          _: "No MFA factor found. Set up two-factor authentication first.",
        },
      });
      return;
    }

    setMfaSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: String(values.code ?? ""),
      });
      if (verifyError) throw verifyError;

      setStep("password");
    } catch (error: unknown) {
      notify(error instanceof Error ? error.message : "crm.mfa.verify_error", {
        type: "warning",
        messageArgs: {
          _: "Invalid code. Please try again.",
        },
      });
    } finally {
      setMfaSubmitting(false);
    }
  };

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      // Prefer the live session (AAL2 after MFA.verify) over URL recovery tokens.
      const {
        data: { session },
      } = await getSupabaseClient().auth.getSession();
      await setPassword({
        access_token: session?.access_token ?? access_token,
        refresh_token: session?.refresh_token ?? refresh_token,
        password: values.password,
      });
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );

      // If password update still requires AAL2, return user to MFA step.
      const message = String(error?.message ?? "");
      if (message.toLowerCase().includes("aal2")) {
        setStep("mfa");
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "loading") {
    return (
      <Layout>
        <p>{translate("crm.common.loading", { _: "Loading..." })}</p>
      </Layout>
    );
  }

  if (step === "mfa") {
    return (
      <Layout>
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("crm.mfa.set_password.title", {
              _: "Verify before changing password",
            })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate("crm.mfa.set_password.subtitle", {
              _: "Enter your two-factor code to continue. This is required because MFA is enabled on your account.",
            })}
          </p>
        </div>
        <Form className="space-y-8" onSubmit={submitMfa}>
          <TextInput
            source="code"
            label="crm.mfa.code"
            validate={required()}
            helperText={false}
            autoComplete="one-time-code"
          />
          <Button
            type="submit"
            className="cursor-pointer w-full"
            disabled={mfaSubmitting || !factorId}
          >
            {translate("crm.mfa.verify.submit", { _: "Verify" })}
          </Button>
        </Form>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.set_password.new_password", {
            _: "Choose your password",
          })}
        </h1>
      </div>
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
        // ra-core Form vs react-hook-form ValidateForm types diverge in strict mode
        validate={validate as never}
      >
        <TextInput
          label={translate("ra.auth.password", {
            _: "Password",
          })}
          autoComplete="new-password"
          source="password"
          type="password"
          validate={required()}
        />
        <TextInput
          label={translate("crm.auth.confirm_password", {
            _: "Confirm password",
          })}
          source="confirmPassword"
          type="password"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("ra.action.save")}
        </Button>
      </Form>
    </Layout>
  );
};

SetPasswordPage.path = "set-password";
